import type { ReferencedEntry } from 'contentful';
import Handlebars from 'handlebars/dist/handlebars.js';
import { useDCSText } from 'hooks/useDCSText';
import React, { useMemo, useCallback } from 'react';

import { getField } from 'utils/EntryUtil';

import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';
import type { IEntryWrapperProps } from '../EntryWrapper/IEntryWrapperProps';
import { MarkdownWrapper, IMarkdownWrapperProps } from '../MarkdownWrapper/MarkdownWrapper';
import type { IFieldWrapperProps } from './IFieldWrapperProps';
import { useReseller } from 'hooks/Reseller/useReseller';

export interface IBlurb<T, E extends HTMLElement = HTMLDivElement, O = {}> extends IFieldWrapperProps<T, E> {
  templateData?: O;
}

function BlurbWithContent<T, E extends HTMLElement = HTMLDivElement, O = {}>({
  entry,
  field,
  source,
  templateData: extraData,
  ...props
}: IBlurb<T, E, O> & IMarkdownWrapperProps<E>) {
  const dcsText = useDCSText(source || '');
  const { resellerTemplate } = useReseller();

  const Wrapped = useCallback(
    applyEntryDataAttributes<
      T,
      ReferencedEntry<T>,
      IMarkdownWrapperProps<E> & IEntryWrapperProps<T, ReferencedEntry<T>>
    >(MarkdownWrapper),
    [MarkdownWrapper],
  );

  const template = useMemo(() => Handlebars.compile(dcsText), [dcsText]);

  const text = useMemo(() => {
    try {
      return template({ shop: resellerTemplate, ...extraData });
    } catch (error) {
      console.error(error);
      return null;
    }
  }, [template, resellerTemplate, extraData]);

  return <Wrapped entry={entry} field={field} source={text} {...props} />;
}

export function Blurb<T, E extends HTMLElement = HTMLDivElement, O = {}>({
  entry,
  field,
  templateData: extraData,
  ...props
}: IBlurb<T, E, O> & IMarkdownWrapperProps<E>) {
  const source = field && (getField(entry, field) as string | undefined);

  if (!source) return null;

  return <BlurbWithContent entry={entry} field={field} templateData={extraData} source={source} {...props} />;
}

Blurb.defaultProps = {
  tagName: 'div',
  templateData: {},
};
